import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Key, Copy, Trash2, AlertCircle, CheckCircle } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { listApiKeys, createApiKey, revokeApiKey, ApiKeyInfo } from '../services/api'

export default function ApiKeysPanel() {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [newKey, setNewKey] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const { data: keys, isLoading } = useQuery({
    queryKey: ['apiKeys'],
    queryFn: async () => (await listApiKeys()).data,
  })

  const createMutation = useMutation({
    mutationFn: (n: string) => createApiKey(n),
    onSuccess: (res) => {
      setNewKey(res.data.key)
      setName('')
      setCopied(false)
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] })
    },
  })

  const revokeMutation = useMutation({
    mutationFn: (id: number) => revokeApiKey(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['apiKeys'] }),
  })

  const copyKey = () => {
    if (newKey) {
      navigator.clipboard.writeText(newKey)
      setCopied(true)
    }
  }

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 mb-1 flex items-center">
        <Key className="h-5 w-5 mr-2 text-primary-600" />
        Customer-Care API Keys
      </h2>
      <p className="text-sm text-gray-600 mb-4">
        Keys let other programs query this app for RN information via{' '}
        <code className="text-xs bg-gray-100 px-1 rounded">GET /api/v1/care/rn/&#123;serial&#125;</code>{' '}
        with an <code className="text-xs bg-gray-100 px-1 rounded">X-API-Key</code> header. Read-only.
      </p>

      {/* One-time key reveal */}
      {newKey && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <div className="flex items-start">
            <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-green-800">
                Key created — copy it now. It will not be shown again.
              </p>
              <div className="flex items-center mt-2 gap-2">
                <code className="flex-1 text-xs font-mono bg-white border border-green-300 rounded px-2 py-1.5 break-all">
                  {newKey}
                </code>
                <button
                  onClick={copyKey}
                  className="flex items-center px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                >
                  <Copy className="h-4 w-4 mr-1" />
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <button onClick={() => setNewKey(null)} className="text-xs text-green-700 mt-2 hover:underline">
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create */}
      <div className="flex items-end gap-2 mb-6">
        <div className="flex-1 max-w-xs">
          <label className="block text-sm font-medium text-gray-700 mb-1">New key name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. billing-portal"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <button
          onClick={() => name.trim() && createMutation.mutate(name.trim())}
          disabled={!name.trim() || createMutation.isPending}
          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
        >
          Create key
        </button>
      </div>

      {createMutation.isError && (
        <div className="flex items-center text-sm text-red-600 mb-4">
          <AlertCircle className="h-4 w-4 mr-1" /> Failed to create key.
        </div>
      )}

      {/* List */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="p-6 text-center text-gray-500 text-sm">Loading…</div>
        ) : !keys || keys.length === 0 ? (
          <div className="p-6 text-center text-gray-500 text-sm">No API keys yet.</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Name', 'Key', 'Status', 'Last Used', 'Created By', ''].map((h) => (
                  <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {keys.map((k: ApiKeyInfo) => (
                <tr key={k.id} className={k.active ? '' : 'opacity-50'}>
                  <td className="px-4 py-2 text-sm font-medium text-gray-900">{k.name}</td>
                  <td className="px-4 py-2 text-sm font-mono text-gray-500">{k.key_prefix}…</td>
                  <td className="px-4 py-2 text-sm">
                    {k.active ? (
                      <span className="text-green-600">active</span>
                    ) : (
                      <span className="text-gray-400">revoked</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-600">
                    {k.last_used_at ? `${formatDistanceToNow(new Date(k.last_used_at))} ago` : 'never'}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-600">{k.created_by ?? '—'}</td>
                  <td className="px-4 py-2 text-right">
                    {k.active && (
                      <button
                        onClick={() => {
                          if (confirm(`Revoke key "${k.name}"? Programs using it will stop working.`)) {
                            revokeMutation.mutate(k.id)
                          }
                        }}
                        disabled={revokeMutation.isPending}
                        className="text-red-600 hover:text-red-800"
                        title="Revoke key"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
